import { nairaFromMinor } from '../../lib/format'
import { addReservationBasketIds } from './reservationBasket'
import { payForOrder } from '../../lib/paystack'
import { commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import { confirmedUpsert, echoUpsert, enumShort, type StoreRecord } from './chefShared'
import type { ChefKitchen, UiDish } from './projections'

/**
 * The rules a planned meal obeys, in one place.
 *
 * A planned meal is a HOLD: an unpaid order with a future `scheduled_at`. That
 * model lives in MealCalendarPage, but the calendar is no longer the only way
 * to reach it — a customer looking at a dish can plan it for a day without
 * going to the planner and finding the dish again.
 *
 * Two entry points to one feature is only an improvement while they agree, and
 * they agree about more than the write: which days are still open, when a hold
 * has to be confirmed by, when a deposit is due, and whether the kitchen even
 * cooks that dish that day. A second screen re-deriving any of that would drift
 * from the calendar the moment either changed — and the customer would be told
 * two different things about the same meal. So the rules are here and both
 * screens read them; neither owns them.
 */

/** Two weeks: the visible week plus the one you can copy into. */
export const PLAN_DAYS = 14

/** Same-day orders stay open until this hour, so "Today" isn't dead from midnight. */
const SAME_DAY_CUTOFF_HOUR = 12

// Party deposit — mirrors the backend DepositConfig so the offline preview and
// the optimistic UI agree with what the server charges (live is authoritative
// via the create_reservation response).
const DEPOSIT_THRESHOLD_MINOR = 10_000_000 // ₦100,000

/** "Party-sized" == deposit-sized: one ₦100,000 threshold for both the deposit
    and the earlier confirm-by, so the two features stay consistent. */
export const LARGE_HOLD_MINOR = DEPOSIT_THRESHOLD_MINOR

/** Party days must confirm this many days before the meal, not just the night before. */
const LARGE_HOLD_LEAD_DAYS = 2
const DEPOSIT_RATE_BPS = 2000 // 20%
const DEPOSIT_FLOOR_MINOR = 500_000 // ₦5,000

export const depositFor = (totalMinor: number): number => {
  if (totalMinor < DEPOSIT_THRESHOLD_MINOR) return 0
  const raw = Math.ceil((totalMinor * DEPOSIT_RATE_BPS) / 10000)
  return Math.min(totalMinor, Math.max(DEPOSIT_FLOOR_MINOR, raw))
}

/** Default delivery hour for callers without an explicit meal slot. */
export const DELIVERY_HOUR = 12

/** The `scheduled_at` a day key resolves to. */
export type MealSlot = 'breakfast' | 'lunch' | 'dinner'
export const MEAL_HOURS: Record<MealSlot, number> = { breakfast: 8, lunch: 12, dinner: 18 }
export const scheduledAtFor = (key: string, slot: MealSlot = 'lunch'): string =>
  `${key}T${String(MEAL_HOURS[slot]).padStart(2, '0')}:00:00+01:00`

export const naira = nairaFromMinor

/** "today" / "tomorrow" / "in 3 days" for a planned day, from the local clock. */
export const relativeDay = (target: Date, now: Date = new Date()): string => {
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const days = Math.round((startOf(target) - startOf(now)) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days < 7) return `in ${days} days`
  if (days < 14) return 'in a week'
  return `in ${Math.round(days / 7)} weeks`
}

export const isoWeekday = (d: Date): number => ((d.getDay() + 6) % 7) + 1

export const startOfDay = (d: Date): Date => {
  const c = new Date(d)
  c.setHours(0, 0, 0, 0)
  return c
}

export const dayKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export const parseKey = (key: string): Date => {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/**
 * Party-aware confirm-by cutoff (epoch ms) for a meal scheduled at `when`.
 * A party-sized day commits `LARGE_HOLD_LEAD_DAYS` earlier; otherwise the
 * cutoff is the start of the meal day. The calendar's day model and its lazy
 * expiry sweep both read this, so the hold shown as expired is exactly the hold
 * that gets swept.
 */
export const holdCutoffMs = (when: Date, party: boolean): number => {
  const c = startOfDay(when)
  if (party) {
    c.setDate(c.getDate() - LARGE_HOLD_LEAD_DAYS)
    return c.getTime()
  }
  // Today is still orderable up to SAME_DAY_CUTOFF_HOUR; every later day is
  // orderable until it begins.
  if (c.getTime() === startOfDay(new Date()).getTime()) {
    c.setHours(SAME_DAY_CUTOFF_HOUR, 0, 0, 0)
    return c.getTime()
  }
  return c.getTime()
}

/** One day of the planning window, before any meals are laid onto it. */
export interface PlanSlot {
  key: string
  date: Date
  /** "Today" / "Tomorrow" / "Thursday". */
  weekday: string
  /** "12 Sep". */
  label: string
  /** confirm-by moment; a held meal past this is expired. */
  cutoff: Date
  expired: boolean
}

/**
 * The days a customer may plan into, oldest first. The calendar layers its
 * holds onto these; the dish page offers them as a list. Both therefore open
 * and close the same days at the same moment.
 */
export const planWindow = (now: Date = new Date()): PlanSlot[] => {
  const today = startOfDay(now)
  const at = now.getTime()
  const slots: PlanSlot[] = []
  for (let i = 0; i < PLAN_DAYS; i += 1) {
    const date = new Date(today)
    date.setDate(today.getDate() + i)
    const cutoff = new Date(holdCutoffMs(date, false))
    slots.push({
      key: dayKey(date),
      date,
      weekday:
        i === 0
          ? 'Today'
          : i === 1
            ? 'Tomorrow'
            : date.toLocaleDateString(undefined, { weekday: 'long' }),
      label: date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
      cutoff,
      expired: at > cutoff.getTime(),
    })
  }
  return slots
}

/**
 * What the customer cannot infer from a price: the day it lands, the hour, the
 * kitchen it travels from — and the one thing that can go wrong, which is a
 * dish or a kitchen that does not do that weekday. Said before the meal is
 * planned rather than discovered on the day.
 */
export interface PlanPreview {
  when: string
  time: string
  from: string
  prep: string
  /** Non-empty when the dish or the kitchen does not normally do that weekday. */
  caution: string
}

const hour12 = (hour: number): string => {
  const suffix = hour >= 12 ? 'pm' : 'am'
  const h = hour % 12 === 0 ? 12 : hour % 12
  return `${h}${suffix}`
}

export const planPreview = (
  dish: UiDish,
  date: Date,
  kitchen: ChefKitchen | undefined,
  slot: MealSlot = 'lunch',
): PlanPreview => {
  const isoDay = isoWeekday(date)
  const weekday = date.toLocaleDateString(undefined, { weekday: 'long' })
  const workingDay = kitchen?.schedule?.days.find((d) => d.day === isoDay) ?? null
  const dishDay = dish.availableDays.length === 0 || dish.availableDays.includes(isoDay)
  return {
    when: `${date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })} · ${relativeDay(date)}`,
    time: `${hour12(MEAL_HOURS[slot])} pickup window`,
    from: kitchen?.area
      ? `Cooked at ${dish.chef}'s kitchen in ${kitchen.area}`
      : `Cooked at ${dish.chef}'s kitchen`,
    prep: dish.cookTimeMinutes > 0 ? `about ${dish.cookTimeMinutes} min to cook` : '',
    // A kitchen with no hours set is unrestricted, so only an explicit closed
    // day is a warning.
    caution: !dishDay
      ? `${dish.chef} does not offer this dish on ${weekday}s.`
      : workingDay && !workingDay.open
        ? `${dish.chef}'s kitchen is normally closed on ${weekday}s — they may not be able to cook this day.`
        : '',
  }
}

/** Orders that occupy a planning day: held (unpaid) or already firm. */
export const FIRM_STATUSES = ['paid', 'accepted', 'preparing', 'ready'] as const

export const isPlanned = (status: string): boolean =>
  status === 'pending_payment' ||
  FIRM_STATUSES.includes(status as (typeof FIRM_STATUSES)[number])

/** Sum of held + firm order totals per calendar day, keyed by dayKey. */
export const dayTotalsByKey = (orders: readonly StoreRecord[]): Map<string, number> => {
  const totals = new Map<string, number>()
  for (const o of orders) {
    if (!isPlanned(enumShort(o.status, ''))) continue
    const raw = o.scheduled_at ? String(o.scheduled_at) : ''
    if (!raw) continue
    const when = new Date(raw)
    if (Number.isNaN(when.getTime())) continue
    const k = dayKey(when)
    totals.set(k, (totals.get(k) ?? 0) + Number(o.total_minor ?? o.subtotal_minor ?? 0))
  }
  return totals
}

/** How many meals are already on each planning day, keyed by dayKey. */
export const mealsPerDay = (orders: readonly StoreRecord[]): Map<string, number> => {
  const counts = new Map<string, number>()
  for (const o of orders) {
    if (!isPlanned(enumShort(o.status, ''))) continue
    const raw = o.scheduled_at ? String(o.scheduled_at) : ''
    if (!raw) continue
    const when = new Date(raw)
    if (Number.isNaN(when.getTime())) continue
    const k = dayKey(when)
    counts.set(k, (counts.get(k) ?? 0) + 1)
  }
  return counts
}

/** Pay an order deposit or balance through server-verified Paystack checkout. */
export const captureHold = async (
  order: StoreRecord,
  amountMinor: number,
  profileId: string,
): Promise<void> => {
  const id = String(order.id ?? '')
  if (amountMinor <= 0 || !id) return
  await payForOrder(id, profileId, amountMinor)
}

export type PlanOutcome =
  | { ok: true; orderId: string; totalMinor: number; depositMinor: number }
  | { ok: false; error: string }

/**
 * Place one hold on a day.
 *
 * A hold is its own order (`metadata.reservation`), created directly — the
 * customer's cart is never touched, so planning a meal for Thursday cannot
 * disturb what they are buying today. That separation is the reason this is
 * safe to offer from the dish page beside "Add to cart": the two actions write
 * to different places and neither can consume the other.
 *
 * In live the row is written only after the command confirms, with the id the
 * server gave it, so the projection replaces the same record rather than
 * duplicating it — and a failure is returned rather than swallowed, because a
 * hold that silently did not happen is a meal the customer thinks they have.
 */
export const planDish = async (input: {
  dish: UiDish
  dayKey: string
  quantity: number
  profileId: string
  mealSlot?: MealSlot
}): Promise<PlanOutcome> => {
  const { dish, dayKey: key, quantity, profileId } = input
  const scheduledAt = scheduledAtFor(key, input.mealSlot)
  if (!Number.isFinite(new Date(scheduledAt).getTime()) || new Date(scheduledAt).getTime() <= Date.now()) {
    return { ok: false, error: 'That meal time has passed. Choose a later meal or another day.' }
  }
  const unitMinor = Math.round(dish.price * 100)
  const totalMinor = unitMinor * quantity
  const localDeposit = depositFor(totalMinor)
  const localId = `hold-${dish.id}-${key}-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`

  const holdRow = (id: string, depositMinor: number): StoreRecord => ({
    id,
    customer_profile_id: profileId,
    merchant_profile_id: dish.chefId,
    status: 'pending_payment',
    scheduled_at: scheduledAt,
    subtotal_minor: totalMinor,
    total_minor: totalMinor,
    amount_paid_minor: 0,
    currency: 'NGN',
    summary: quantity > 1 ? `${dish.name} ×${quantity}` : dish.name,
    item_name: dish.name,
    reference: `RES-${Math.random().toString(36).slice(2, 7).toUpperCase()}`,
    created_at: new Date().toISOString(),
    metadata: {
      reservation: true,
      deposit_minor: depositMinor,
      dish_name: dish.name,
      dish_id: dish.id,
      dish_image: dish.image || '',
      chef_name: dish.chef || '',
      chef_id: dish.chefId || '',
    },
  })

  if (!isLiveRuntime()) {
    echoUpsert('marketplace.marketplace_orders', holdRow(localId, localDeposit))
    return { ok: true, orderId: localId, totalMinor, depositMinor: localDeposit }
  }

  let response
  try {
    response = await commands.marketplace.createReservation({
      customer_profile_id: profileId,
      merchant_profile_id: dish.chefId,
      item_ref: dish.id,
      item_name: dish.name,
      quantity,
      unit_amount_minor: unitMinor,
      currency: 'NGN',
      scheduled_at: scheduledAt,
      metadata: {
        dish_name: dish.name,
        dish_id: dish.id,
        dish_image: dish.image || '',
        chef_name: dish.chef || '',
      },
    })
  } catch {
    response = undefined
  }
  const row = commandPayload(response)
  const orderId = String(row.id ?? '')
  if (!orderId) {
    return {
      ok: false,
      error: `${dish.name} could not be planned. Check your connection and try again.`,
    }
  }
  // The server sizes the deposit; the local estimate is only the offline preview.
  const depositMinor = row.deposit_minor != null ? Number(row.deposit_minor) : localDeposit
  const held = holdRow(orderId, depositMinor)
  confirmedUpsert('marketplace.marketplace_orders', {
    ...held, ...row,
    metadata: { ...held.metadata as Record<string, unknown>, ...row.metadata as Record<string, unknown> },
  })
  return { ok: true, orderId, totalMinor: Number(row.total_minor ?? totalMinor), depositMinor }
}

/** One planned meal, as the cart materialiser needs it. */
export interface PlannedMeal {
  /** The dish, when the plan still points at one that exists. */
  dish?: UiDish
  /** Falls back to the name the hold carries when the dish is gone. */
  dishName: string
  quantity: number
  /** The hold's own id, so a meal already paid for is not added again. */
  holdId: string
  balanceMinor?: number
}

export type BasketOutcome =
  | { ok: true; added: number; totalMinor: number }
  | { ok: false; error: string; added: number }

/** Select existing reservations for one cart payment. Delivery dates remain on their original orders. */
export const addPlanToCart = async (
  meals: readonly PlannedMeal[],
  profileId: string,
): Promise<BasketOutcome> => {
  const unique = [...new Map(meals.filter((meal) => meal.holdId).map((meal) => [meal.holdId, meal])).values()]
  if (unique.length === 0) return { ok: false, added: 0, error: 'There is nothing in your plan to order yet.' }
  try {
    addReservationBasketIds(profileId, unique.map((meal) => meal.holdId))
    return {
      ok: true,
      added: unique.length,
      totalMinor: unique.reduce((sum, meal) => sum + (meal.balanceMinor ?? 0), 0),
    }
  } catch (error) {
    return { ok: false, added: 0, error: error instanceof Error ? error.message : 'Your planned meals could not be added to the cart.' }
  }
}
